import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Building2, User, Mail, Phone, MessageSquare, Users, Truck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    company_name: '',
    phone: '',
    message: '',
    account_type: 'customer', // 'customer' or 'supplier'
    accept_terms: false,
    accept_privacy: false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.accept_terms || !formData.accept_privacy) {
      toast.error('Accepteer de voorwaarden om door te gaan');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/request-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.full_name,
          email: formData.email,
          company_name: formData.company_name,
          phone: formData.phone || null,
          message: formData.message || null,
          account_type: formData.account_type
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        let errorMsg = 'Aanvraag indienen mislukt';
        try {
          const data = await response.clone().json();
          if (typeof data.detail === 'string') errorMsg = data.detail;
        } catch {
          if (response.status === 400) errorMsg = 'Dit e-mailadres is al geregistreerd';
          else if (response.status === 422) errorMsg = 'Controleer uw gegevens en probeer opnieuw';
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error('Aanvraag indienen mislukt. Probeer het later opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen w-full relative overflow-hidden">
        {/* Same gradient background */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, #ff9a56 0%, #ff6b8a 25%, #c44eff 50%, #7b68ee 75%, #00bfff 100%)`,
              backgroundSize: '400% 400%',
              animation: 'gradientShift 15s ease infinite'
            }}
          />
        </div>

        <header className="relative z-10 p-6">
          <Link to="/" className="text-2xl font-bold tracking-tight text-black hover:opacity-80 transition-opacity">
            Spooker
          </Link>
        </header>

        <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] px-4">
          <div className="w-full max-w-md">
            <div className="bg-white rounded-xl shadow-2xl p-8 text-center">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                Aanvraag ontvangen
              </h1>
              <p className="text-neutral-600 mb-6">
                Bedankt voor uw aanmelding! Wij nemen zo snel mogelijk contact met u op om uw account te activeren.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#635bff] hover:bg-[#4b45c6] text-white"
              >
                Terug naar inloggen
              </Button>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full relative overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, #ff9a56 0%, #ff6b8a 25%, #c44eff 50%, #7b68ee 75%, #00bfff 100%)`,
            backgroundSize: '400% 400%',
            animation: 'gradientShift 15s ease infinite'
          }}
        />
        <svg 
          className="absolute bottom-0 right-0 w-full h-full opacity-30"
          viewBox="0 0 1440 900" 
          preserveAspectRatio="xMaxYMax slice"
        >
          <path 
            fill="url(#wave1)" 
            d="M0,900 C300,700 600,800 900,600 C1200,400 1440,500 1440,0 L1440,900 Z"
          />
          <defs>
            <linearGradient id="wave1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#ff6b8a" />
              <stop offset="100%" stopColor="#c44eff" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Header */}
      <header className="relative z-10 p-6">
        <Link to="/" className="text-2xl font-bold tracking-tight text-black hover:opacity-80 transition-opacity">
          Spooker
        </Link>
      </header>

      {/* Register Card */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-120px)] px-4 py-8">
        <div className="w-full max-w-lg">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="p-8">
              <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
                Account aanvragen
              </h1>
              <p className="text-neutral-600 mb-6">
                Vul onderstaand formulier in en wij nemen contact met u op.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Account Type Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-neutral-700">
                    Ik wil mij aanmelden als *
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleChange('account_type', 'customer')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        formData.account_type === 'customer'
                          ? 'border-[#635bff] bg-[#635bff]/5'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        formData.account_type === 'customer' ? 'bg-[#635bff]/10' : 'bg-neutral-100'
                      }`}>
                        <Users className={`h-5 w-5 ${
                          formData.account_type === 'customer' ? 'text-[#635bff]' : 'text-neutral-500'
                        }`} />
                      </div>
                      <span className={`text-sm font-medium ${
                        formData.account_type === 'customer' ? 'text-[#635bff]' : 'text-neutral-700'
                      }`}>
                        Klant
                      </span>
                      <span className="text-xs text-neutral-500 text-center">
                        Zoek leveranciers
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('account_type', 'supplier')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        formData.account_type === 'supplier'
                          ? 'border-[#635bff] bg-[#635bff]/5'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        formData.account_type === 'supplier' ? 'bg-[#635bff]/10' : 'bg-neutral-100'
                      }`}>
                        <Truck className={`h-5 w-5 ${
                          formData.account_type === 'supplier' ? 'text-[#635bff]' : 'text-neutral-500'
                        }`} />
                      </div>
                      <span className={`text-sm font-medium ${
                        formData.account_type === 'supplier' ? 'text-[#635bff]' : 'text-neutral-700'
                      }`}>
                        Leverancier
                      </span>
                      <span className="text-xs text-neutral-500 text-center">
                        Bied diensten aan
                      </span>
                    </button>
                  </div>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Volledige naam *
                  </Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    required
                    placeholder="Jan de Vries"
                    className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg"
                    data-testid="register-name-input"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Zakelijk e-mailadres *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    placeholder="jan@bedrijf.nl"
                    className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg"
                    data-testid="register-email-input"
                  />
                </div>

                {/* Company */}
                <div className="space-y-2">
                  <Label htmlFor="company_name" className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Bedrijfsnaam *
                  </Label>
                  <Input
                    id="company_name"
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    required
                    placeholder="Bedrijf B.V."
                    className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg"
                    data-testid="register-company-input"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Telefoonnummer
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+31 6 12345678"
                    className="h-12 border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg"
                    data-testid="register-phone-input"
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-medium text-neutral-700 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Bericht (optioneel)
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    placeholder="Vertel ons meer over uw organisatie en behoeften..."
                    rows={3}
                    className="border-2 border-neutral-200 focus:border-[#635bff] focus:ring-[#635bff] rounded-lg resize-none"
                    data-testid="register-message-input"
                  />
                </div>

                {/* Terms & Privacy */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="accept_terms"
                      checked={formData.accept_terms}
                      onCheckedChange={(checked) => handleChange('accept_terms', checked)}
                      className="mt-0.5 border-neutral-300 data-[state=checked]:bg-[#635bff] data-[state=checked]:border-[#635bff]"
                      data-testid="register-terms-checkbox"
                    />
                    <Label htmlFor="accept_terms" className="text-sm text-neutral-600 cursor-pointer leading-relaxed">
                      Ik ga akkoord met de{' '}
                      <Link to="/terms" className="text-[#635bff] hover:underline">
                        algemene voorwaarden
                      </Link>
                      *
                    </Label>
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="accept_privacy"
                      checked={formData.accept_privacy}
                      onCheckedChange={(checked) => handleChange('accept_privacy', checked)}
                      className="mt-0.5 border-neutral-300 data-[state=checked]:bg-[#635bff] data-[state=checked]:border-[#635bff]"
                      data-testid="register-privacy-checkbox"
                    />
                    <Label htmlFor="accept_privacy" className="text-sm text-neutral-600 cursor-pointer leading-relaxed">
                      Ik ga akkoord met het{' '}
                      <Link to="/privacy" className="text-[#635bff] hover:underline">
                        privacybeleid
                      </Link>
                      *
                    </Label>
                  </div>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full h-12 bg-[#635bff] hover:bg-[#4b45c6] text-white font-medium rounded-lg transition-colors"
                  disabled={loading || !formData.accept_terms || !formData.accept_privacy}
                  data-testid="register-submit-button"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Aanvraag indienen'
                  )}
                </Button>
              </form>
            </div>

            {/* Card Footer */}
            <div className="px-8 py-4 bg-neutral-50 border-t border-neutral-100">
              <p className="text-center text-sm text-neutral-600">
                Al een account?{' '}
                <Link
                  to="/login"
                  className="text-[#635bff] hover:text-[#4b45c6] font-medium"
                  data-testid="login-link"
                >
                  Inloggen
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 p-6 flex items-center justify-between text-sm text-neutral-600">
        <span>© 2026 Spooker</span>
        <div className="flex gap-4">
          <Link to="/privacy" className="hover:text-neutral-900">Privacy</Link>
          <Link to="/terms" className="hover:text-neutral-900">Voorwaarden</Link>
        </div>
      </footer>

      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

export default RegisterPage;
